import { Currencies } from "@finway-group/shared/lib/consts"
import { CurrencyEnum } from "@finway-group/shared/lib/models"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import { isAmountBelowCurrencyReasonableLimit } from "@finway-group/shared/lib/utils/validators/currencyLimit.validator"
import { FormInstance, Rule } from "antd/lib/form"
import { Moment } from "moment-timezone"
import { parseCurrencyInput } from "~Components/currencyInput/config"
import i18n from "~Shared/locales/i18n"
import { capitalizeFirstLetter } from "~Shared/utils/helper.utils"

interface NumberOptions {
    arePositiveNumbersAllowed: boolean
    areNegativeNumbersAllowed: boolean
}

interface CheckDateInterface {
    formInstance: FormInstance
    value: Moment
    name: string
    minText?: string
    maxText?: string
}

export const checkMinDate = ({ formInstance, value, name, minText = "", maxText = "" }: CheckDateInterface) => {
    const maxDate = formInstance.getFieldValue(name)

    if (maxDate && value && value.clone().startOf("day").isAfter(maxDate.clone().startOf("day"))) {
        return Promise.reject(
            i18n.t("validation:date_before", {
                attribute: capitalizeFirstLetter(i18n.t(minText || "input:filter_form.starting_date")),
                compare: i18n.t(maxText || "input:filter_form.ending_date"),
            }),
        )
    }

    return Promise.resolve()
}

export const checkMaxDate = ({ formInstance, value, name, minText = "", maxText = "" }: CheckDateInterface) => {
    const minDate = formInstance.getFieldValue(name)

    if (minDate && value && value.clone().startOf("day").isBefore(minDate.clone().startOf("day"))) {
        return Promise.reject(
            i18n.t("validation:date_after", {
                attribute: capitalizeFirstLetter(i18n.t(minText || "input:filter_form.ending_date")),
                compare: i18n.t(maxText || "input:filter_form.starting_date"),
            }),
        )
    }

    return Promise.resolve()
}

export const checkMinNumber = ({
    formInstance,
    value,
    name,
    minText = "",
    maxText = "",
    limit = undefined,
    numberOptions = { arePositiveNumbersAllowed: true, areNegativeNumbersAllowed: false },
}: {
    formInstance: FormInstance
    value: any
    name: string
    minText?: string
    maxText?: string
    limit?: number
    numberOptions?: NumberOptions
}) => {
    value = parseAmountFilterValue(value)
    if (value || value === 0) {
        const maxAmount = formInstance.getFieldValue(name)
        if (isNegativeAmountWhenOnlyPositiveAllowed(numberOptions, value)) {
            return Promise.reject(new Error(i18n.t("validation:min.number", { min: 0 })))
        }
        if (isPositiveAmountWhenOnlyNegativeAllowed(numberOptions, value)) {
            return Promise.reject(new Error(i18n.t("validation:min.number_negative", { min: 0 })))
        }
        if (limit && value > limit) {
            return Promise.reject(new Error(i18n.t("validation:max.number", { max: limit })))
        }
        if (maxAmount && value > Number(maxAmount)) {
            return Promise.reject(
                i18n.t("validation:bigger_amount", {
                    attribute: i18n.t(minText || "input:filter_form.min_amount"),
                    compare: i18n.t(maxText || "input:filter_form.max_amount"),
                }),
            )
        }
    }
    return Promise.resolve()
}

export const checkMaxNumber = ({
    formInstance,
    value,
    name,
    minText = "",
    maxText = "",
    limit = undefined,
    numberOptions = { arePositiveNumbersAllowed: true, areNegativeNumbersAllowed: false },
}: {
    formInstance: FormInstance
    value: any
    name: string
    minText?: string
    maxText?: string
    limit?: number
    numberOptions?: NumberOptions
}) => {
    value = parseAmountFilterValue(value)
    if (value || value === 0) {
        const minAmount = formInstance.getFieldValue(name)
        if (isNegativeAmountWhenOnlyPositiveAllowed(numberOptions, value)) {
            return Promise.reject(new Error(i18n.t("validation:min.number", { min: 0 })))
        }
        if (isPositiveAmountWhenOnlyNegativeAllowed(numberOptions, value)) {
            return Promise.reject(new Error(i18n.t("validation:min.number_negative", { min: 0 })))
        }
        if (limit && value > limit) {
            return Promise.reject(new Error(i18n.t("validation:max.number", { max: limit })))
        }
        if (minAmount && value < Number(minAmount)) {
            return Promise.reject(
                i18n.t("validation:smaller_amount", {
                    attribute: i18n.t(minText || "input:filter_form.min_amount"),
                    compare: i18n.t(maxText || "input:filter_form.max_amount"),
                }),
            )
        }
    }
    return Promise.resolve()
}

const parseAmountFilterValue = (value: any) => (value !== "" ? Number(value) : value)

const isNegativeAmountWhenOnlyPositiveAllowed = (options: NumberOptions, value: number): boolean =>
    options.arePositiveNumbersAllowed && !options.areNegativeNumbersAllowed && value < 0
const isPositiveAmountWhenOnlyNegativeAllowed = (options: NumberOptions, value: number): boolean =>
    !options.arePositiveNumbersAllowed && options.areNegativeNumbersAllowed && value >= 0

export const checkDueInDays = (value: any) => {
    if (value !== "undefined" && Number(value) < 0) {
        return Promise.reject(new Error(i18n.t("validation:min.number", { min: 0 })))
    }

    return Promise.resolve()
}

export const isWithinCurrencyLimit = (currency: CurrencyEnum): Rule => ({
    validator: (_rule, inputValue: string) => {
        const value = parseCurrencyInput(inputValue)

        const maxValue = Currencies[currency].max_allowed_value
        const minValue = -maxValue
        if (!isAmountBelowCurrencyReasonableLimit(currency, value)) {
            return Promise.reject(new Error(i18n.t("validation:max.number", { max: formatCurrencyNumber({ value: maxValue, currency, showFullNumber: false, maxDigits: 7 }) })))
        }
        if (!isAmountBelowCurrencyReasonableLimit(currency, -value)) {
            return Promise.reject(
                new Error(i18n.t("validation:min.number_very_short", { min: formatCurrencyNumber({ value: minValue, currency, showFullNumber: false, maxDigits: 7 }) })),
            )
        }
        return Promise.resolve()
    },
})
